import { useEffect, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../ui/Button';
import { ArrowPathIcon, XMarkIcon } from '@heroicons/react/24/outline';

/**
 * PWA Update Prompt
 *
 * Listens for service worker updates and shows a banner when a new version
 * is available. User can click to update and reload.
 *
 * Usage: Add <PWAUpdatePrompt /> to App.tsx
 */
export function PWAUpdatePrompt() {
  const [show, setShow] = useState(false);

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(registration) {
      console.log('PWA: Service Worker registered', registration);
    },
    onRegisterError(error) {
      console.error('PWA: Service Worker registration error', error);
    },
  });

  useEffect(() => {
    if (needRefresh) {
      setShow(true);
    }
  }, [needRefresh]);

  const handleUpdate = () => {
    setShow(false);
    updateServiceWorker(true); // true = reload page after update
  };

  const handleDismiss = () => {
    setShow(false);
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed bottom-20 md:bottom-6 left-4 right-4 md:left-auto md:right-6 md:max-w-md z-50"
        >
          <div className="bg-white dark:bg-surface-800 rounded-lg shadow-lg border border-surface-200 dark:border-surface-700 p-4">
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <h3 className="font-semibold text-navy-800 dark:text-gold-500 mb-1">
                  Update Available
                </h3>
                <p className="text-sm text-surface-600 dark:text-surface-400 mb-3">
                  A new version of Daily Reprieve is ready. Update now for the latest features and fixes.
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={handleUpdate}
                    leftIcon={<ArrowPathIcon className="w-4 h-4" />}
                  >
                    Update Now
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={handleDismiss}
                  >
                    Later
                  </Button>
                </div>
              </div>
              <button
                onClick={handleDismiss}
                className="text-surface-400 hover:text-surface-600 dark:hover:text-surface-300 transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default PWAUpdatePrompt;
